/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps
} from '../../../../client/js/components/form/fields/SearchInput'
import { findManyCities } from '../../lib/rest/cityAPIClient'
import { CityOption, getCityOptionLabel } from './CityInput'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { Badge, Box, Group } from '@mantine/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'

export type CitiesInputProps =
  Omit<SearchInputProps<City>, 'data' | 'onChange' | 'onSearchChange' | 'value'>
  & {
  queryFilter?: Record<string, any>,
  onChange (value?: string[]): void,
  value?: string[]
}

function CitiesInput (props: CitiesInputProps) {
  const intl = useIntl()
  const {
    id,
    label,
    name = 'cities',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['cities', queryFilter, searchParams],
    queryFn: () => findManyCities({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.cities : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<City>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getCityOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const selectionQuery = useQuery({
    enabled: value != null && value.length > 0,
    queryKey: ['cities', value],
    queryFn: () => findManyCities({
      ids: value
    })
  })
  const selection = useMemo(() => (
    (selectionQuery.data != null
      ? getHalEmbedded(selectionQuery.data)?.cities
      : (value != null ? value.map((el): Partial<City> => ({ id: parseInt(el, 10) })) : null)) || []
  ), [selectionQuery.data, value])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const handleChange = useCallback((cityId?: string) => {
    if (cityId == null) {
      onChange([])
    } else {
      onChange(value != null
        ? Array.from(new Set([...value, cityId]))
        : [cityId])
    }
    setSearch(null)
  }, [onChange, setSearch, value])

  const handleRemove = useCallback((cityId: string) => {
    return () => {
      onChange((value || []).filter((el) => el !== cityId))
    }
  }, [onChange, value])

  return (
    <Box>
      <SearchInput
        id={fieldId}
        label={label || intl.formatMessage({
          id: 'cities',
          defaultMessage: 'Villes'
        })}
        {...others}
        data={options}
        name={name}
        onChange={handleChange}
        onSearchChange={setSearch}
        renderOption={CityOption}
        required={required}
        searchValue={searchParams.search}
        value={value != null ? String(value) : null}
      />

      <Group gap="xs" mt="xs">
        {selection?.map((city) => (
          <Badge
            key={city.id}
            onClick={handleRemove(String(city.id))}
            role="button"
            size="xs"
            variant="light"
          >
            {city.name}
            <FontAwesomeIcon
              fixedWidth
              icon={faTimes}
            />
          </Badge>
        ))}
      </Group>
    </Box>
  )
}

export default CitiesInput
